import { bool, oneOfType, shape, string } from 'prop-types'
import { Component, createRef } from 'react'

import store, { subscribe } from 'shared/store'
import { actionNames } from 'shared/store/ducks/userSession'
import blockUI from 'views/assets/scripts/blockUI'
import CmsMobileMenu from 'views/components/organisms/CmsMobileMenu'
import OffCanvas from 'views/components/organisms/OffCanvas'
import buildClientAPI from 'views/providers/clientAPI'
import cssVars from 'views/utils/cssVars'
import { offCanvasCategory, shopLanguagesType } from 'views/utils/propTypes'

const animationDurationInMs = cssVars.timings['animation-duration-ms']

class OffCanvasContainer extends Component {
  static animation = anim => `${animationDurationInMs}ms ${anim} ease-in-out`
  static findById = (id, collection) => collection.find(d => d.id === id)
  static enhanceData = data => ({
    ...data,
    style: { animation: OffCanvasContainer.animation('slideIn') },
  })

  isComponentWithCmsData = () => {
    const { cmsMenu } = this.props
    return !!cmsMenu && Object.keys(cmsMenu).length >= 1
  }

  constructor(props) {
    super(props)

    const categoriesForVisibleStack = this.isComponentWithCmsData()
      ? props.cmsMobileCategories
      : props.categories

    const visibleStack = [{ ...categoriesForVisibleStack, style: null }]

    this.state = {
      allData: [],
      loading: false,
      hidden: props.hidden,
      userData: props.userData,
      visibleStack,
    }

    this.container = createRef()
  }

  componentDidMount() {
    subscribe.after.once(actionNames.setUserSession, ({ userSession }) => {
      this.setState({ userData: userSession })
    })
  }

  async fetchClassicData(id) {
    // if data exists, no need to fetch
    const existingData = OffCanvasContainer.findById(id, this.state.allData)
    if (existingData) {
      return existingData
    }
    const unblockUI = blockUI({ target: this.container.current })
    const { clientAPIService } = buildClientAPI()

    try {
      return clientAPIService.fetchOffCanvasCategories(id)
    } catch (err) {
      throw new Error(err)
    } finally {
      unblockUI()
    }
  }

  async fetchCmsData(categoryPath, fetchByPath) {
    const existingData = OffCanvasContainer.findById(
      categoryPath,
      this.state.allData
    )

    if (existingData) {
      return existingData
    }
    const unblockUI = blockUI({ target: this.container.current })
    const { clientAPIService } = buildClientAPI()
    try {
      return clientAPIService.fetchOffCanvasCategories(
        categoryPath,
        fetchByPath
      )
    } catch (err) {
      throw new Error(err)
    } finally {
      unblockUI()
    }
  }

  addNewData = data => {
    const dataWithAnim = OffCanvasContainer.enhanceData(data)
    const { visibleStack } = this.state

    this.setState({
      visibleStack: [...visibleStack, dataWithAnim],
    })
  }

  updateAllData(data, id) {
    const { allData } = this.state
    const hasData = OffCanvasContainer.findById(id, allData)
    const updateAllData = hasData ? allData : [...allData, data]

    this.setState({
      allData: updateAllData,
    })
  }

  cmsGoNextHandler =
    (id, href, options = {}) =>
    async e => {
      e.preventDefault()
      try {
        const fetchByPath = options.fetchByPath || false
        const categoryIdentifier = fetchByPath ? href : id
        const data = await this.fetchCmsData(categoryIdentifier, fetchByPath)
        if (data?.subcategories.length) {
          this.addNewData(data)
          this.updateAllData(data, id)
        } else {
          this.redirectTo(href)
        }
      } catch (error) {
        this.redirectTo(href)
      }
    }

  classicGoNextHandler = (id, href) => async e => {
    e.preventDefault()

    try {
      const data = await this.fetchClassicData(id)

      if (data?.subcategories.length) {
        this.addNewData(data)
        this.updateAllData(data, id)
      } else {
        this.redirectTo(href)
      }
    } catch (error) {
      this.redirectTo(href)
    }
  }

  goBackHandler = () => {
    const { visibleStack } = this.state
    const updatedVisibleStack = [...visibleStack]
    updatedVisibleStack[updatedVisibleStack.length - 1].style = {
      animation: OffCanvasContainer.animation('slideOut'),
    }
    this.setState({ visibleStack: updatedVisibleStack })
    setTimeout(
      () => this.setState({ visibleStack: visibleStack.slice(0, -1) }),
      animationDurationInMs
    )
  }

  closeMenuHandler = () => store.dispatch({ type: 'CLOSE_OFFCANVAS' })

  redirectTo = href => {
    window.location.href = href
  }

  render() {
    const { language } = this.props
    const { visibleStack, loading, hidden, userData } = this.state
    const { goBackHandler, closeMenuHandler } = this

    const shouldRenderComponentWithCmsData = this.isComponentWithCmsData()

    const goNextHandler = shouldRenderComponentWithCmsData
      ? this.cmsGoNextHandler
      : this.classicGoNextHandler

    const props = {
      visibleStack,
      loading,
      hidden,
      userData,
      goNextHandler,
      goBackHandler,
      closeMenuHandler,
      language,
    }

    // TODO: currently we don't support yet a combination of the new header from BlueBerries + CMSMenu. Coming Soon ;)
    if (shouldRenderComponentWithCmsData) {
      return (
        <CmsMobileMenu
          {...props}
          onShowStaticPage={this.addNewData}
          visibleStack={visibleStack}
          ref={this.container}
        />
      )
    }

    return <OffCanvas {...props} ref={this.container} />
  }
}

OffCanvasContainer.propTypes = {
  cmsMenu: shape({}),
  cmsMobileCategories: shape({}),
  menuLinks: shape({}),
  categories: offCanvasCategory.isRequired,
  language: oneOfType([string, shopLanguagesType]),
  userData: shape({ isLoggedIn: bool, name: string }),
  hidden: bool,
  enableNewHeader: bool,
}

OffCanvasContainer.defaultProps = {
  menuLinks: {},
  language: null,
  userData: null,
  hidden: false,
  enableNewHeader: false,
  cmsMenu: {},
}

export default OffCanvasContainer
